
// --- START: Conditional Logger ---
// To enable logging, add `?debug=true` to the URL.
const NALA_DEBUG_MODE = new URLSearchParams(window.location.search).get('debug') === 'true';

const Logger = {
  log: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.log(...args);
    }
  },
  warn: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.warn(...args);
    }
  },
  // Errors are always logged regardless of debug mode.
  error: (...args) => {
    console.error(...args);
  },
  group: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.group(...args);
    }
  },
  groupEnd: () => {
    if (NALA_DEBUG_MODE) {
      console.groupEnd();
    }
  }
};
// --- END: Conditional Logger ---

// Cart Drawer Enhancements - Rebuy Features Replication
// Configurable settings at the top for easy editing

/**
 * CartWatcher - Observes cart changes using PerformanceObserver
 * Based on: https://mikefallows.com/posts/observing-cart-changes-in-a-shopify-theme/
 * 
 * WHY THIS IS BETTER:
 * - Uses PerformanceObserver to detect cart API calls (no fetch interception needed)
 * - Works with any script/app that modifies the cart (not just our code)
 * - Compares old vs new cart state to detect actual changes (not just requests)
 * - Emits clean events that other code can listen to
 * - No race conditions from trying to intercept fetch at the right time
 * 
 * HOW IT WORKS:
 * 1. Stores current cart state in localStorage
 * 2. Uses PerformanceObserver to detect any /cart/* requests
 * 3. After detecting a request, fetches fresh cart state
 * 4. Compares old vs new to find what actually changed
 * 5. Emits 'cart_changed' event with oldCart, newCart, and changes
 */
class CartWatcher {
  constructor() {
    this.storageKey = 'cart_watcher_last_cart';
    this.cartObserver = null;
    this.lastTriggeringRequest = null;
    this.requestHistory = [];
    this.MAX_HISTORY = 10;
  }

  init() {
    // Initialize with current cart state
    this.emitCartChanges().then(() => {
      this.observeCartChanges();
    });
  }

  async fetchCart() {
    const fetchFn = window._originalFetch || window.fetch;
    const response = await fetchFn(`${routes.cart_url}.js`);
    if (!response.ok) {
      Logger.error('[CartWatcher] Failed to fetch cart');
      return null;
    }
    return await response.json();
  }

  storeCart(cart) {
    if (!cart) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cart));
    } catch (e) {
      Logger.warn('[CartWatcher] Failed to store cart:', e);
    }
  }

  getStoredCart() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : { items: [] };
    } catch (e) {
      Logger.warn('[CartWatcher] Failed to get stored cart:', e);
      return { items: [] };
    }
  }

  findCartChanges(oldCart, newCart) {
    if (!oldCart || !oldCart.items) oldCart = { items: [] };
    if (!newCart || !newCart.items) newCart = { items: [] };

    const onlyInLeft = (l, r) => l.filter(li => !r.some(ri => li.key === ri.key));
    
    let result = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
    };

    // Check for quantity changes in existing items
    oldCart.items.forEach(oi => {
      const ni = newCart.items.find(i => i.key === oi.key && i.quantity !== oi.quantity);
      if (!ni) return;
      
      let quantity = ni.quantity - oi.quantity;
      let item = { ...ni };
      item.quantity = Math.abs(quantity);
      
      if (quantity > 0) {
        result.added.push(item);
      } else {
        result.removed.push(item);
      }
    });

    return result;
  }

  async emitCartChanges() {
    const timestamp = Date.now();
    const newCart = await this.fetchCart();
    if (!newCart) return;

    const oldCart = this.getStoredCart();
    const changes = this.findCartChanges(oldCart, newCart);

    // Only emit event if there are actual changes
    if (changes.added.length > 0 || changes.removed.length > 0) {
      // DETAILED LOGGING: Identify who/what removed items
      Logger.group(`[CartWatcher][${timestamp}] 🔍 CART CHANGE DETECTED`);
      
      // Log what triggered this check
      if (this.lastTriggeringRequest) {
        const timeSinceRequest = timestamp - this.lastTriggeringRequest.timestamp;
        Logger.log('📡 Triggered by request:', {
          url: this.lastTriggeringRequest.url,
          type: this.lastTriggeringRequest.initiatorType,
          timeSinceRequest: timeSinceRequest + 'ms',
          requestDuration: this.lastTriggeringRequest.duration?.toFixed(2) + 'ms'
        });
      } else {
        Logger.log('📡 Triggered by: Initial check or manual call');
      }
      
      // Log OLD cart state
      Logger.log('📦 OLD CART STATE:', {
        itemCount: oldCart.item_count || 0,
        total: oldCart.total_price ? (oldCart.total_price / 100).toFixed(2) : '0.00',
        items: (oldCart.items || []).map(item => ({
          handle: item.handle,
          title: item.product_title,
          quantity: item.quantity,
          key: item.key,
          isFreeGift: item.properties?._free_gift === 'true',
          isMysteryGift: item.properties?._mystery_gift === 'true',
          price: (item.price / 100).toFixed(2)
        }))
      });
      
      // Log NEW cart state
      Logger.log('📦 NEW CART STATE:', {
        itemCount: newCart.item_count || 0,
        total: (newCart.total_price / 100).toFixed(2),
        items: (newCart.items || []).map(item => ({
          handle: item.handle,
          title: item.product_title,
          quantity: item.quantity,
          key: item.key,
          isFreeGift: item.properties?._free_gift === 'true',
          isMysteryGift: item.properties?._mystery_gift === 'true',
          price: (item.price / 100).toFixed(2)
        }))
      });
      
      // DETAILED: What was ADDED
      if (changes.added.length > 0) {
        Logger.log('➕ ITEMS ADDED:', changes.added.map(item => ({
          handle: item.handle,
          title: item.product_title,
          quantity: item.quantity,
          key: item.key,
          isFreeGift: item.properties?._free_gift === 'true',
          price: (item.price / 100).toFixed(2),
          properties: item.properties
        })));
      }
      
      // DETAILED: What was REMOVED (THIS IS KEY!)
      if (changes.removed.length > 0) {
        Logger.log('❌ ITEMS REMOVED:', changes.removed.map(item => ({
          handle: item.handle,
          title: item.product_title,
          quantity: item.quantity,
          key: item.key,
          variantId: item.variant_id,
          isFreeGift: item.properties?._free_gift === 'true',
          isMysteryGift: item.properties?._mystery_gift === 'true',
          price: (item.price / 100).toFixed(2),
          properties: item.properties,
          fullItem: item // Include full item for deep inspection
        })));
        
        // Check if free gift was removed
        const freeGiftRemoved = changes.removed.find(item => 
          item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties?._free_gift === 'true'
        );
        
        if (freeGiftRemoved) {
          // Check for known bundle apps that might be removing items
          const bundleAppActive = typeof window.MAKEBECOOL_BUNDLES !== 'undefined';
          const rebuyActive = typeof window.Rebuy !== 'undefined';
          
          Logger.error('🚨 FREE GIFT WAS REMOVED!', {
            item: freeGiftRemoved,
            triggeredBy: this.lastTriggeringRequest?.url || 'unknown',
            requestType: this.lastTriggeringRequest?.isModifying ? 'MODIFYING' : 'READ',
            oldCartTotal: oldCart.total_price ? (oldCart.total_price / 100).toFixed(2) : '0.00',
            newCartTotal: (newCart.total_price / 100).toFixed(2),
            timeSinceLastRequest: this.lastTriggeringRequest ? 
              (timestamp - this.lastTriggeringRequest.timestamp) + 'ms' : 'N/A',
            suspectScripts: {
              MAKEBECOOL_BUNDLES: bundleAppActive ? '⚠️ ACTIVE - This app often uses /cart/update.js and may remove unrecognized items' : 'not detected',
              Rebuy: rebuyActive ? '⚠️ ACTIVE' : 'not detected'
            },
            investigation: {
              checkUpdateRequest: this.lastTriggeringRequest?.url?.includes('/cart/update') ? 
                '⚠️ /cart/update.js detected - Bundle apps typically use this' : 'No /cart/update.js',
              recentRequests: this.requestHistory.slice(-3).map(r => r.url.split('?')[0])
            }
          });
        }
      }
      
      Logger.log('📊 SUMMARY:', {
        added: changes.added.length,
        removed: changes.removed.length,
        newItemCount: newCart.item_count,
        newTotal: (newCart.total_price / 100).toFixed(2),
        freeGiftInOldCart: (oldCart.items || []).some(item => 
          item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties?._free_gift === 'true'
        ),
        freeGiftInNewCart: (newCart.items || []).some(item => 
          item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties?._free_gift === 'true'
        )
      });
      
      Logger.groupEnd();
      
      const event = new CustomEvent("cart_changed", { 
        detail: {
          oldCart,
          newCart,
          changes,
          triggeringRequest: this.lastTriggeringRequest
        } 
      });
      window.dispatchEvent(event);
      
      // Clear the triggering request after logging
      this.lastTriggeringRequest = null;
    }

    this.storeCart(newCart);
    return newCart;
  }

  observeCartChanges() {
    // Check if PerformanceObserver is supported
    if (typeof PerformanceObserver === 'undefined') {
      Logger.warn('[CartWatcher] PerformanceObserver not supported, falling back to polling');
      // Fallback: poll every 2 seconds (only if needed)
      setInterval(() => this.emitCartChanges(), 2000);
      return;
    }

    this.cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
        const isCartChangeRequest = /\/cart\//.test(entry.name);
        
        if (isValidRequestType && isCartChangeRequest) {
          // Check if this looks like a bundle app request (do this first)
          const isUpdateRequest = entry.name.includes('/cart/update');
          const isAddRequest = entry.name.includes('/cart/add');
          const isChangeRequest = entry.name.includes('/cart/change');
          
          // Track ALL cart requests (including reads) to see the sequence
          const requestInfo = {
            url: entry.name,
            timestamp: Date.now(),
            duration: entry.duration,
            initiatorType: entry.initiatorType,
            startTime: entry.startTime,
            isModifying: !entry.name.includes('/cart.js') && !entry.name.includes('sections='),
            requestType: isUpdateRequest ? 'UPDATE (often bundle apps)' : 
                        isAddRequest ? 'ADD' : 
                        isChangeRequest ? 'CHANGE (quantity/remove)' : 
                        entry.name.includes('/cart.js') ? 'READ' : 'UNKNOWN'
          };
          
          // Add to history
          this.requestHistory.push(requestInfo);
          if (this.requestHistory.length > this.MAX_HISTORY) {
            this.requestHistory.shift();
          }
          
          // Only process modifying requests
          if (requestInfo.isModifying) {
            // Store information about this triggering request
            this.lastTriggeringRequest = requestInfo;
            
            Logger.log('[CartWatcher] 🔍 Cart-modifying request detected:', {
              url: entry.name,
              type: entry.initiatorType,
              duration: entry.duration?.toFixed(2) + 'ms',
              time: new Date().toISOString(),
              requestType: requestInfo.requestType,
              warning: isUpdateRequest ? '⚠️ /cart/update.js is often used by bundle apps like MAKEBECOOL_BUNDLES' : null
            });
            
            // Log recent request history to see sequence
            if (this.requestHistory.length > 1) {
              Logger.log('[CartWatcher] Recent request sequence:', 
                this.requestHistory.slice(-5).map((r, idx) => ({
                  seq: idx + 1,
                  url: r.url.split('?')[0], // Remove query params for readability
                  time: r.timestamp,
                  timeSinceLast: idx > 0 ? (r.timestamp - this.requestHistory[this.requestHistory.length - 6 + idx]?.timestamp) + 'ms' : '0ms'
                }))
              );
            }
            
            // The cart update is now atomic. We only need a short delay to ensure
            // the 'emit' happens after the current execution stack is clear.
            setTimeout(() => {
              this.emitCartChanges();
            }, 50);
          } else {
            // Log read requests too, but don't process
            Logger.log('[CartWatcher] 📖 Cart read request:', {
              url: entry.name.split('?')[0],
              type: entry.initiatorType
            });
          }
        }
      });
    });

    try {
      this.cartObserver.observe({ entryTypes: ["resource"] });
      Logger.log('[CartWatcher] Started observing cart changes');
    } catch (e) {
      Logger.error('[CartWatcher] Failed to observe:', e);
    }
  }

  destroy() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
  }
}

const CART_ENHANCEMENT_CONFIG = {
  // Free gift product handle - change this to switch the gift product
  // Using the Cosmetic Bag (Hot Mess Kit) - variant ID: 49254513672425
  FREE_GIFT_HANDLE: 'hot-mess-kit-free-gift',
  FREE_GIFT_VARIANT_ID: '49254513672425', // Hardcoded variant ID as fallback
  
  // Mystery gift product handle
  MYSTERY_GIFT_HANDLE: 'mystery-gift',
  
  // Mystery gift eligibility - URL parameters that trigger eligibility
  MYSTERY_GIFT_URL_TRIGGERS: ['gap flow', 'gap+flow'],
  
  // Mystery gift cookie name and duration (24 hours)
  MYSTERY_GIFT_COOKIE_NAME: 'mystery_gift',
  MYSTERY_GIFT_COOKIE_DURATION_HOURS: 24,
  
  // Timer duration in minutes
  TIMER_DURATION_MINUTES: 10,
  
  // Tier thresholds in cents (100 = $1)
  TIERS: {
    AUD: {
      TIER_1: 15000,  // $150 - Free Express Shipping
      TIER_2: 18000,  // $180 - Free Cosmetic Bag
      TIER_1_LABEL: 'Free Express Shipping',
      TIER_2_LABEL: 'Free Cosmetic Bag',
      TIER_1_MESSAGE: (remaining) => `You Are $${remaining} Away From Free Express Shipping!`,
      TIER_2_MESSAGE: (remaining) => `You Are $${remaining} Away From Free Cosmetic Bag!`,
      TIER_1_COMPLETE: "You've Unlocked Free Express Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Free Cosmetic Bag!"
    },
    NZD: {
      TIER_1: 15000,  // $150 - Free Shipping
      TIER_2: 18000,  // $180 - Free Cosmetic Bag
      TIER_1_LABEL: 'Free Shipping',
      TIER_2_LABEL: 'Free Cosmetic Bag',
      TIER_1_MESSAGE: (remaining) => `You Are $${remaining} Away From Free Shipping!`,
      TIER_2_MESSAGE: (remaining) => `You Are $${remaining} Away From Free Cosmetic Bag!`,
      TIER_1_COMPLETE: "You've Unlocked Free Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Free Cosmetic Bag!"
    }
  },
  
  // Supported currencies for progress bar (only show for these)
  SUPPORTED_CURRENCIES: ['AUD', 'NZD']
};

// Helper function to get color swatch URL
function getColorSwatchURL(variantName) {
  if (!variantName) return null;
  const baseurl = 'https://cdn.shopify.com/s/files/1/0651/7083/3641/files/';
  let sanitizedVariantName = variantName.replace(/['&®]/g, '').replace(/\s+/g, '-').toLowerCase();
  sanitizedVariantName = sanitizedVariantName.replace(/\//g, '-');
  return `${baseurl}${sanitizedVariantName}.jpg`;
}

// Timer Management Class - Global timer that runs continuously
class CartTimer {
  constructor() {
    this.intervalId = null;
    this.STORAGE_KEY = 'cart_drawer_timer_end';
    this.DURATION = CART_ENHANCEMENT_CONFIG.TIMER_DURATION_MINUTES * 60 * 1000;
    this.endTime = null;
    this.isRunning = false;
  }

  init() {
    // If already running, don't restart
    if (this.isRunning && this.intervalId) {
      return;
    }
    
    // Get or set end time
    const storedEndTime = this.getEndTime();
    if (storedEndTime && storedEndTime > Date.now()) {
      this.endTime = storedEndTime;
    } else {
      this.endTime = Date.now() + this.DURATION;
      localStorage.setItem(this.STORAGE_KEY, this.endTime.toString());
    }
    
    // Start the countdown
    this.start();
  }

  getEndTime() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  }

  start() {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    this.isRunning = true;
    const self = this;
    
    // Update function
    const tick = function() {
      const minutesEl = document.getElementById('cart-timer-minutes');
      const secondsEl = document.getElementById('cart-timer-seconds');
      
      if (!minutesEl || !secondsEl) {
        return;
      }
      
      const now = Date.now();
      let remaining = Math.max(0, self.endTime - now);
      
      if (remaining <= 0) {
        // Timer expired - reset
        self.endTime = Date.now() + self.DURATION;
        localStorage.setItem(self.STORAGE_KEY, self.endTime.toString());
        remaining = self.DURATION;
      }
      
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      
      minutesEl.textContent = minutes.toString().padStart(2, '0');
      secondsEl.textContent = seconds.toString().padStart(2, '0');
    };
    
    // Run immediately
    tick();
    
    // Then run every second
    this.intervalId = setInterval(tick, 1000);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  destroy() {
    this.stop();
  }
}

// Progress Bar Management Class
class CartProgressBar {
  constructor() {
    // State tracking
    this.state = {
      percentage: 0,
      tier1Complete: false,
      tier2Complete: false,
      promptText: '',
      isVisible: false,
      currency: null
    };
    
    // DOM element cache (populated on first use or after DOM change)
    this.elements = null;
    this.elementsVersion = 0;
    
    // Debounce timer for rapid updates
    this.updateDebounceTimer = null;
    
    // Setup MutationObserver to detect DOM changes and re-apply state
    this.setupDOMObserver();
  }
  
  setupDOMObserver() {
    // Observe the cart drawer for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check if progress bar elements were replaced
      const progressFill = document.getElementById('cart-progress-bar-fill');
      if (progressFill && this.state.percentage > 0) {
        // Check if the fill width doesn't match our state
        const currentWidth = parseFloat(progressFill.style.width) || 0;
        if (Math.abs(currentWidth - this.state.percentage) > 1) {
          // DOM was replaced, re-apply state immediately without transition
          this.applyStateToDOM(true);
        }
      }
    });
    
    // Start observing when cart drawer exists
    const startObserving = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        observer.observe(cartDrawer, { 
          childList: true, 
          subtree: true 
        });
      } else {
        // Retry after a short delay
        setTimeout(startObserving, 100);
      }
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving);
    } else {
      startObserving();
    }
  }
  
  /**
   * Get or cache DOM elements. Returns null if critical elements missing.
   * Uses version tracking to detect when elements need re-querying.
   */
  getElements(forceRefresh = false) {
    // Check if we need to refresh (forced or elements missing)
    if (forceRefresh || !this.elements || !document.body.contains(this.elements.section)) {
      const section = document.getElementById('cart-drawer-progress-section');
      
      if (!section) {
        this.elements = null;
        return null;
      }
      
      this.elements = {
        section,
        fill: document.getElementById('cart-progress-bar-fill'),
        prompt: document.getElementById('cart-progress-bar-prompt'),
        meter: document.getElementById('cart-progress-bar-meter'),
        step1: document.getElementById('cart-progress-step-1'),
        step2: document.getElementById('cart-progress-step-2'),
        label1: document.getElementById('cart-progress-label-1'),
        label2: document.getElementById('cart-progress-label-2'),
        timerHeader: document.getElementById('cart-drawer-timer-header')
      };
      
      this.elementsVersion++;
      
      // Apply current state to fresh elements immediately (prevents flash)
      this.applyStateToDOM(true);
    }
    
    return this.elements;
  }
  
  /**
   * Force re-query of DOM elements (call after cart drawer HTML is replaced)
   */
  reinitElements() {
    this.getElements(true);
  }
  
  /**
   * Apply current state to DOM elements
   * @param {boolean} immediate - If true, skip transition for instant update
   */
  applyStateToDOM(immediate = false) {
    // Re-query elements directly to ensure we have fresh references
    const fill = document.getElementById('cart-progress-bar-fill');
    const step1 = document.getElementById('cart-progress-step-1');
    const step2 = document.getElementById('cart-progress-step-2');
    const prompt = document.getElementById('cart-progress-bar-prompt');
    const meter = document.getElementById('cart-progress-bar-meter');
    const section = document.getElementById('cart-drawer-progress-section');
    const timerHeader = document.getElementById('cart-drawer-timer-header');
    
    // Update visibility
    if (section) {
      section.style.display = this.state.isVisible ? 'block' : 'none';
    }
    if (timerHeader) {
      timerHeader.style.display = this.state.isVisible ? 'flex' : 'none';
    }
    
    if (!this.state.isVisible) {
      // Remove updating class even if not visible
      if (section) section.classList.remove('is-updating');
      return;
    }
    
    // Update progress bar fill
    if (fill) {
      if (immediate) {
        fill.style.transition = 'none';
        fill.style.width = `${this.state.percentage}%`;
        // Force reflow then restore transition
        fill.offsetHeight;
        // Restore transition after a micro-task
        requestAnimationFrame(() => {
          fill.style.transition = 'width 0.3s ease';
        });
      } else {
        fill.style.transition = 'width 0.3s ease';
        fill.style.width = `${this.state.percentage}%`;
      }
    }
    
    // Update tier icons
    if (step1) {
      step1.classList.toggle('complete', this.state.tier1Complete);
    }
    if (step2) {
      step2.classList.toggle('complete', this.state.tier2Complete);
    }
    
    // Update prompt text
    if (prompt) {
      prompt.textContent = this.state.promptText;
    }
    
    // Update ARIA
    if (meter) {
      meter.setAttribute('aria-valuenow', Math.round(this.state.percentage));
    }
    
  }
  
  /**
   * Calculate cart total excluding free gift items
   */
  calculateCartTotal(cart) {
    if (!cart || !cart.items) return 0;
    
    let total = cart.total_price;
    const freeGiftHandle = CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE;
    
    for (const item of cart.items) {
      if (item.handle === freeGiftHandle && 
          item.properties && 
          item.properties._free_gift === 'true') {
        total -= (item.price * item.quantity);
      }
    }
    
    return Math.max(0, total);
  }
  
  /**
   * Calculate progress state from cart data
   * Returns new state object without modifying DOM
   */
  calculateState(cart) {
    const currency = cart?.currency || 'AUD';
    const isSupported = CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency);
    const hasItems = cart && cart.item_count > 0;
    
    // Determine visibility
    const isVisible = isSupported && hasItems;
    
    if (!isVisible) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: '',
        isVisible: false,
        currency
      };
    }
    
    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: '',
        isVisible: false,
        currency
      };
    }
    
    const cartTotal = this.calculateCartTotal(cart);
    
    let percentage = 0;
    let promptText = '';
    let tier1Complete = false;
    let tier2Complete = false;
    
    if (cartTotal < tiers.TIER_1) {
      // Before tier 1: Progress 0% to 25% (icon 1 at 25%)
      const remaining = tiers.TIER_1 - cartTotal;
      percentage = (cartTotal / tiers.TIER_1) * 25;
      promptText = tiers.TIER_1_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = false;
      tier2Complete = false;
      
    } else if (cartTotal < tiers.TIER_2) {
      // Between tier 1 and tier 2: Progress 25% to 75% (icon 2 at 75%)
      const remaining = tiers.TIER_2 - cartTotal;
      const progressInTier2 = (cartTotal - tiers.TIER_1) / (tiers.TIER_2 - tiers.TIER_1);
      percentage = 25 + (progressInTier2 * 50);
      promptText = tiers.TIER_2_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = true;
      tier2Complete = false;
      
    } else {
      // Reached tier 2: Fill to 100%
      percentage = 100;
      promptText = tiers.TIER_2_COMPLETE;
      tier1Complete = true;
      tier2Complete = true;
    }
    
    return {
      percentage: Math.min(100, Math.max(0, percentage)),
      tier1Complete,
      tier2Complete,
      promptText,
      isVisible: true,
      currency
    };
  }
  
  /**
   * Update tier labels based on currency
   */
  updateLabels(currency) {
    const el = this.elements;
    if (!el) return;
    
    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) return;
    
    if (el.label1) el.label1.textContent = tiers.TIER_1_LABEL;
    if (el.label2) el.label2.textContent = tiers.TIER_2_LABEL;
  }
  
  /**
   * Update visibility of progress section and timer header
   */
  updateVisibility(isVisible) {
    const el = this.elements;
    if (!el) return;
    
    if (el.section) {
      el.section.style.display = isVisible ? 'block' : 'none';
    }
    if (el.timerHeader) {
      el.timerHeader.style.display = isVisible ? 'flex' : 'none';
    }
  }

  /**
   * Main update method - calculates state and updates DOM
   * @param {Object} cart - Shopify cart object
   * @param {boolean} forceRefresh - Force DOM element refresh
   */
  update(cart, forceRefresh = false) {
    // Get/refresh elements
    const el = this.getElements(forceRefresh);
    
    if (!el) {
      Logger.warn('CartProgressBar: Progress section not found in DOM');
      return;
    }
    
    // Calculate new state
    const newState = this.calculateState(cart);
    
    // Check if state actually changed (avoid unnecessary DOM updates)
    const stateChanged = 
      this.state.percentage !== newState.percentage ||
      this.state.tier1Complete !== newState.tier1Complete ||
      this.state.tier2Complete !== newState.tier2Complete ||
      this.state.promptText !== newState.promptText ||
      this.state.isVisible !== newState.isVisible ||
      this.state.currency !== newState.currency;
    
    if (!stateChanged && !forceRefresh) {
      return; // No changes, skip DOM updates
    }
    
    // Store new state
    this.state = newState;
    
    // Update visibility
    this.updateVisibility(newState.isVisible);
    
    if (!newState.isVisible) {
      return; // Hidden, no need to update content
    }
    
    // Update labels if currency changed
    if (this.state.currency !== newState.currency || forceRefresh) {
      this.updateLabels(newState.currency);
    }
    
    // Apply state to DOM with animation
    this.applyStateToDOM(false);
  }
}

// Free Gift Manager Class - Simplified for the new atomic update flow
class FreeGiftManager {
  constructor() {
    this.isProcessing = false;
    this.productCache = null;
    this.productCacheTime = 0;
    this.PRODUCT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Main entry point, called by the cart_changed event.
   * Trusts the cart object it receives and acts immediately.
   */
  async checkAndManage(cart) {
    if (this.isProcessing || !cart) {
      return;
    }

    this.isProcessing = true;
    Logger.log(`[FreeGiftManager] Checking cart state...`);

    try {
      const currency = cart.currency || 'AUD';
      if (!CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency)) {
        this.isProcessing = false;
        return;
      }

      const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
      if (!tiers) {
        this.isProcessing = false;
        return;
      }
      
      const freeGiftItem = this.findFreeGiftInCart(cart);
      const hasGift = !!freeGiftItem;
      const cartTotal = this.calculateCartTotalExcludingGift(cart);
      const regularItemCount = this.countRegularItems(cart);
      const shouldHaveGift = cartTotal >= tiers.TIER_2 && regularItemCount > 0;

      Logger.log(`[FreeGiftManager] State: shouldHaveGift=${shouldHaveGift}, hasGift=${hasGift}`);

      if (shouldHaveGift && !hasGift) {
        Logger.log(`[FreeGiftManager] Action: Adding gift.`);
        await this._addFreeGift();
      } else if (!shouldHaveGift && hasGift) {
        Logger.log(`[FreeGiftManager] Action: Removing gift.`);
        await this._removeFreeGift(freeGiftItem);
      }
    } catch (error) {
      Logger.error('[FreeGiftManager] Error:', error);
    } finally {
      this.isProcessing = false;
      Logger.log(`[FreeGiftManager] Check complete.`);
    }
  }

  /**
   * Helper to calculate cart total excluding free gift
   */
  calculateCartTotalExcludingGift(cart) {
    if (!cart || !cart.items) return 0;
    
    let total = cart.total_price;
    for (const item of cart.items) {
      if (item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties && 
          item.properties._free_gift === 'true') {
        total -= (item.price * item.quantity);
      }
    }
    return Math.max(0, total);
  }

  /**
   * Helper to check if free gift is in cart
   */
  findFreeGiftInCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(item => 
      item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
      item.properties && 
      item.properties._free_gift === 'true'
    );
  }

  /**
   * Helper to count regular (non-gift) items
   */
  countRegularItems(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.filter(item => 
      !(item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
        item.properties && 
        item.properties._free_gift === 'true')
    ).length;
  }

  /**
   * Get product data for the free gift, with caching.
   */
  async _getProductData() {
    const fetchFn = window._originalFetch || window.fetch;
    const productHandle = CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE;
    const productUrl = `/products/${productHandle}.json`;
    
    if (this.productCache && (Date.now() - this.productCacheTime) < this.PRODUCT_CACHE_TTL) {
      return this.productCache;
    }
    
    try {
      const response = await fetchFn(productUrl);
      if (!response.ok) {
        Logger.error(`[FreeGiftManager] Failed to fetch product data for ${productHandle}.`);
        return null;
      }
      const productData = await response.json();
      this.productCache = productData.product;
      this.productCacheTime = Date.now();
      return this.productCache;
    } catch (error) {
      Logger.error(`[FreeGiftManager] Error fetching product data:`, error);
      return null;
    }
  }

  /**
   * Adds the free gift to the cart. Relies on CartWatcher to trigger UI refresh.
   */
  async _addFreeGift() {
    const fetchFn = window._originalFetch || window.fetch;
    let variantId = CART_ENHANCEMENT_CONFIG.FREE_GIFT_VARIANT_ID;
    const product = await this._getProductData();
    
    if (product) {
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (variant) {
        variantId = variant.id.toString();
      }
    }

    const addUrl = routes.cart_add_url.endsWith('.js') 
      ? routes.cart_add_url 
      : `${routes.cart_add_url}.js`;
    
    await fetchFn(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' }
      })
    });
    // NOTE: No UI refresh call is needed here.
    // The CartWatcher will detect this cart modification and emit an event,
    // which triggers the main UI update logic.
  }

  /**
   * Removes the free gift from the cart. Relies on CartWatcher to trigger UI refresh.
   */
  async _removeFreeGift(giftItem) {
    if (!giftItem || !giftItem.key) return;
    
    const fetchFn = window._originalFetch || window.fetch;
    // Use /cart/update.js for consistency with our main cart logic.
    await fetchFn(`${routes.cart_update_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: {
          [giftItem.key]: 0
        }
      })
    });
    // NOTE: No UI refresh call is needed here.
    // The CartWatcher will detect this cart modification and emit an event.
  }
}

// Free Gift Display Manager
// Free Gift Display - No longer needed (gift displayed in line items)
class FreeGiftDisplay {
  update(cart) {
    // Free gift is now displayed in the line items, not in a separate section
  }
}

// Mystery Gift Manager Class
// Handles mystery gift eligibility, selection, and cart management
class MysteryGiftManager {
  constructor() {
    this.mysteryGiftProduct = null;
    this.selectedVariant = null;
    this.isEligible = false;
    this.isAddingToCart = false;
    this.isRemovingFromCart = false;
    
    // Initialize on construction
    this.init();
  }
  
  init() {
    // Check for email campaign on load
    this.checkForEmailCampaign();
    
    // Fetch the mystery gift product
    this.fetchMysteryGiftProduct();
  }

  /**
   * Check URL parameters for email campaign triggers
   * Sets a cookie if found
   */
  checkForEmailCampaign() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams) {
        const searchString = urlParams.toString().toLowerCase();
        const triggers = CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_URL_TRIGGERS;
        
        for (const trigger of triggers) {
          if (searchString.includes(trigger.toLowerCase())) {
            // Set cookie for configured duration
            const expiryDate = new Date();
            expiryDate.setTime(expiryDate.getTime() + CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_DURATION_HOURS * 60 * 60 * 1000);
            document.cookie = `${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/`;
            Logger.log('[MysteryGift] Email campaign detected, cookie set');
            return true;
          }
        }
      }
    } catch (error) {
      Logger.error('[MysteryGift] Error checking email campaign:', error);
    }
    return false;
  }
  
  /**
   * Check if mystery gift cookie exists
   */
  hasCookie() {
    return document.cookie.split(';').some((item) => 
      item.trim().startsWith(`${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=`)
    );
  }
  
  /**
   * Check if mystery gift is already in cart
   */
  hasGiftInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some((item) => 
      item.properties && item.properties._mystery_gift === 'true'
    );
  }
  
  /**
   * Get mystery gift item from cart
   */
  getGiftFromCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find((item) => 
      item.properties && item.properties._mystery_gift === 'true'
    );
  }
  
  /**
   * Fetch the mystery gift product data
   */
  async fetchMysteryGiftProduct() {
    try {
      const fetchFn = window._originalFetch || window.fetch;
      
      // Try direct product fetch first
      const directResponse = await fetchFn(`/products/${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_HANDLE}.json`);
      if (directResponse.ok) {
        const data = await directResponse.json();
        this.mysteryGiftProduct = data.product;
        Logger.log('[MysteryGift] Product fetched:', this.mysteryGiftProduct?.title);
        return;
      }
      
      // Fallback: search through products.json pages
      let page = 1;
      const maxPages = 5;
      
      while (page <= maxPages) {
        const response = await fetchFn(`/products.json?limit=250&page=${page}`);
        if (!response.ok) break;
        
        const data = await response.json();
        const product = data.products.find((p) => p.handle === CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_HANDLE);
        
        if (product) {
          this.mysteryGiftProduct = product;
          Logger.log('[MysteryGift] Product found on page', page);
          return;
        }
        
        if (data.products.length < 250) break; // No more pages
        page++;
      }
      
      Logger.log('[MysteryGift] Product not found');
    } catch (error) {
      Logger.error('[MysteryGift] Error fetching product:', error);
    }
  }
  
  /**
   * Check eligibility for mystery gift
   * Returns true if user is eligible and gift is not already in cart
   */
  checkEligibility(cart) {
    // Must have cookie from email campaign
    const hasCookie = this.hasCookie();
    
    // Must have items in cart
    const hasItems = cart && cart.item_count > 0;
    
    // Must not already have mystery gift in cart
    const alreadyInCart = this.hasGiftInCart(cart);
    
    // Must have product data loaded
    const hasProduct = this.mysteryGiftProduct !== null;
    
    this.isEligible = hasCookie && hasItems && !alreadyInCart && hasProduct;
    
    Logger.log('[MysteryGift] Eligibility check:', {
      hasCookie,
      hasItems,
      alreadyInCart,
      hasProduct,
      isEligible: this.isEligible
    });
    
    return this.isEligible;
  }
  
  /**
   * Get available variants for selection
   */
  getAvailableVariants() {
    if (!this.mysteryGiftProduct || !this.mysteryGiftProduct.variants) {
      return [];
    }
    return this.mysteryGiftProduct.variants.filter(v => v.available !== false);
  }
  
  /**
   * Add mystery gift to cart with selected variant
   */
  async addToCart(variantId) {
    if (this.isAddingToCart || this.isRemovingFromCart) {
      Logger.log('[MysteryGift] Operation in progress, skipping');
      return false;
    }
    
    if (!variantId) {
      Logger.error('[MysteryGift] No variant ID provided');
      return false;
    }
    
    try {
      this.isAddingToCart = true;
      const fetchFn = window._originalFetch || window.fetch;
      
      // Find the variant
      const variant = this.mysteryGiftProduct?.variants?.find(v => v.id.toString() === variantId.toString());
      if (variant) {
        this.selectedVariant = variant;
      }
      
      const response = await fetchFn(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId.toString(),
          quantity: 1,
          properties: {
            _mystery_gift: 'true'
          }
        })
      });
      
      if (response.ok) {
        Logger.log('[MysteryGift] Added to cart');
        
        // Hide selection UI
        this.hideSelectionUI();
        
        // Wait for Shopify to process
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Refresh cart drawer
        const cartResponse = await fetchFn(`${routes.cart_url}.js`);
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          await refreshCartDrawerWithReinit(cart.items.length);
          
          // Update all UI components
          if (window.cartDrawerEnhancements) {
            window.cartDrawerEnhancements.progressBar.update(cart, true);
            window.cartDrawerEnhancements.itemCounter.reinitElements();
            window.cartDrawerEnhancements.itemCounter.update(cart);
          }
        }
        
        return true;
      } else {
        Logger.error('[MysteryGift] Failed to add to cart:', response.status);
        return false;
      }
    } catch (error) {
      Logger.error('[MysteryGift] Error adding to cart:', error);
      return false;
    } finally {
      this.isAddingToCart = false;
    }
  }
  
  /**
   * Remove mystery gift from cart (when cart becomes empty or ineligible)
   */
  async removeFromCart(cart) {
    if (this.isRemovingFromCart || this.isAddingToCart) {
      return false;
    }
    
    const giftItem = this.getGiftFromCart(cart);
    if (!giftItem) {
      return false;
    }
    
    try {
      this.isRemovingFromCart = true;
      const fetchFn = window._originalFetch || window.fetch;
      
      const response = await fetchFn('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });
      
      if (response.ok) {
        Logger.log('[MysteryGift] Removed from cart');
        this.selectedVariant = null;
        
        // Wait for Shopify to process
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Refresh cart drawer
        const cartResponse = await fetchFn(`${routes.cart_url}.js`);
        if (cartResponse.ok) {
          const updatedCart = await cartResponse.json();
          await refreshCartDrawerWithReinit(updatedCart.items.length);
          
          // Update all UI components
          if (window.cartDrawerEnhancements) {
            window.cartDrawerEnhancements.progressBar.update(updatedCart, true);
            window.cartDrawerEnhancements.itemCounter.reinitElements();
            window.cartDrawerEnhancements.itemCounter.update(updatedCart);
          }
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      Logger.error('[MysteryGift] Error removing from cart:', error);
      return false;
    } finally {
      this.isRemovingFromCart = false;
    }
  }
  
  /**
   * Check and manage mystery gift based on cart state
   */
  async checkAndManage(cart) {
    // Skip if operations in progress
    if (this.isAddingToCart || this.isRemovingFromCart) {
      return;
    }
    
    // Remove if cart is empty or has only the mystery gift
    if (!cart || cart.item_count === 0) {
      return;
    }
    
    // Count non-mystery-gift items
    const regularItemCount = cart.items.filter(item => {
      return !(item.properties && item.properties._mystery_gift === 'true');
    }).length;
    
    // If only mystery gift in cart (no regular items), remove it
    if (regularItemCount === 0 && this.hasGiftInCart(cart)) {
      await this.removeFromCart(cart);
      return;
    }
    
    // Check eligibility and show/hide selection UI
    const isEligible = this.checkEligibility(cart);
    
    if (isEligible) {
      this.showSelectionUI();
    } else {
      this.hideSelectionUI();
    }
  }
  
  /**
   * Show the mystery gift selection UI
   */
  showSelectionUI() {
    const container = document.getElementById('mystery-gift-selection');
    if (container) {
      container.style.display = 'block';
      this.renderSelectionUI();
    }
  }
  
  /**
   * Hide the mystery gift selection UI
   */
  hideSelectionUI() {
    const container = document.getElementById('mystery-gift-selection');
    if (container) {
      container.style.display = 'none';
    }
  }
  
  /**
   * Render the mystery gift selection UI content
   */
  renderSelectionUI() {
    const container = document.getElementById('mystery-gift-selection');
    if (!container || !this.mysteryGiftProduct) return;
    
    const variants = this.getAvailableVariants();
    if (variants.length === 0) return;
    
    // Get product image
    const productImage = this.mysteryGiftProduct.images?.[0]?.src || 
                         this.mysteryGiftProduct.image?.src || '';
    
    container.innerHTML = `
      <div class="mystery-gift-selection__inner">
        <div class="mystery-gift-selection__image">
          ${productImage ? `<img src="${productImage}" alt="${this.mysteryGiftProduct.title}" />` : ''}
        </div>
        <div class="mystery-gift-selection__content">
          <h4 class="mystery-gift-selection__title">CLAIM YOUR FREE MYSTERY GIFT!</h4>
          <p class="mystery-gift-selection__subtitle">Select your size:</p>
          <select id="mystery-gift-variant-select" class="mystery-gift-selection__select">
            <option value="">Choose size...</option>
            ${variants.map(v => `<option value="${v.id}">${v.title}</option>`).join('')}
          </select>
          <button id="mystery-gift-add-btn" class="mystery-gift-selection__button" disabled>
            ADD TO CART
          </button>
        </div>
      </div>
    `;
    
    // Add event listeners
    const select = container.querySelector('#mystery-gift-variant-select');
    const button = container.querySelector('#mystery-gift-add-btn');
    
    if (select && button) {
      select.addEventListener('change', (e) => {
        button.disabled = !e.target.value;
      });
      
      button.addEventListener('click', async () => {
        const variantId = select.value;
        if (variantId) {
          button.disabled = true;
          button.textContent = 'ADDING...';
          await this.addToCart(variantId);
        }
      });
    }
  }
}

// Item Counter Manager
class ItemCounter {
  constructor() {
    // State tracking
    this.state = {
      count: 0,
      isVisible: false
    };
    
    this.reinitElements();
  }
  
  reinitElements() {
    this.counterElement = document.getElementById('cart-drawer-item-counter');
    this.countElement = document.getElementById('cart-item-count');
  }
  
  update(cart) {
    if (!cart) return;
    
    // Just update the count text - don't manipulate display
    const countElement = document.getElementById('cart-item-count');
    if (countElement) {
      countElement.textContent = cart.item_count;
    }
    
    // Update state for reference
    this.state.count = cart.item_count;
    this.state.isVisible = cart.item_count > 0;
  }
}

// Helper to refresh cart drawer with proper custom element reinitialization
async function refreshCartDrawerWithReinit(expectedItemCount = null, retryCount = 0) {
  const fetchFn = window._originalFetch || window.fetch;
  const maxRetries = 2;
  
  try {
    const response = await fetchFn(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) {
      Logger.error('[Refresh] Failed to fetch sections, status:', response.status);
      return;
    }
    
    const data = await response.json();
    const cartDrawer = document.querySelector('cart-drawer');
    
    // Shopify returns sections at root level, not under 'sections' key
    const cartDrawerHtml = data['cart-drawer'];
    const cartBubbleHtml = data['cart-icon-bubble'];
    
    if (cartDrawerHtml && cartDrawer) {
      // Store state before update
      const wasActive = cartDrawer.classList.contains('active');
      const scrollTop = cartDrawer.querySelector('.drawer__inner')?.scrollTop || 0;
      
      // Parse the new HTML to count items
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cartDrawerHtml;
      const newCartItems = tempDiv.querySelectorAll('.cart-item');
      
      // If expected count provided and doesn't match, retry after delay
      if (expectedItemCount !== null && newCartItems.length !== expectedItemCount && retryCount < maxRetries) {
        Logger.log(`[Refresh] HTML has ${newCartItems.length} items but expected ${expectedItemCount}, retrying...`);
        await new Promise(resolve => setTimeout(resolve, 400));
        return refreshCartDrawerWithReinit(expectedItemCount, retryCount + 1);
      }
      
      // Get the new cart-drawer element from the parsed HTML
      const newSection = tempDiv.querySelector('cart-drawer') || tempDiv.firstElementChild;
      
      if (newSection) {
          // Replace just the cart-drawer innerHTML to preserve the element itself
          const newInnerContent = newSection.innerHTML;
          cartDrawer.innerHTML = newInnerContent;
        
        // Restore active state
        if (wasActive) {
          cartDrawer.classList.add('active', 'animate');
        }
        
        // Restore scroll position
        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner) {
          drawerInner.scrollTop = scrollTop;
        }
        
        // Update empty state based on actual items
        const hasItems = cartDrawer.querySelector('.cart-item') !== null;
        cartDrawer.classList.toggle('is-empty', !hasItems);
        
        const drawerItems = cartDrawer.querySelector('cart-drawer-items');
        if (drawerItems) {
          drawerItems.classList.toggle('is-empty', !hasItems);
        }
        
        // Re-setup the overlay click handler
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => cartDrawer.close());
        }
        
        // Verify the update
        const actualCartItems = cartDrawer.querySelectorAll('.cart-item');
        if (expectedItemCount !== null && actualCartItems.length !== expectedItemCount) {
          Logger.warn('[Refresh] Item count mismatch after update:', actualCartItems.length, 'vs expected', expectedItemCount);
        }
      } else {
        Logger.warn('[Refresh] newSection not found in parsed HTML');
      }
    } else {
      Logger.warn('[Refresh] Missing cart drawer elements');
    }
    
    // Update cart icon bubble
    if (cartBubbleHtml) {
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cartBubbleHtml;
        const newContent = tempDiv.querySelector('.shopify-section') || tempDiv.firstElementChild;
        if (newContent) {
          cartBubble.innerHTML = newContent.innerHTML;
        }
      }
    }
  } catch (error) {
    Logger.error('[Refresh] Error refreshing cart drawer:', error);
  }
}

// Helper to refresh cart drawer content by triggering Shopify's native section rendering
async function refreshCartDrawerContent() {
  try {
    const fetchFn = window._originalFetch || window.fetch;
    
    // Fetch fresh sections from Shopify
    const response = await fetchFn(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) {
      Logger.error('Failed to fetch cart sections');
      return false;
    }
    
    const data = await response.json();
    
    // Try to use CartDrawerItems native rendering if available
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.getSectionsToRender === 'function') {
      // Use native section rendering
      const sections = cartDrawerItems.getSectionsToRender();
      sections.forEach((section) => {
        const sectionHtml = data.sections[section.section];
        if (sectionHtml) {
          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || 
                                   document.getElementById(section.id);
          if (elementToReplace) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const newElement = doc.querySelector(section.selector) || doc.querySelector('.shopify-section');
            if (newElement) {
              elementToReplace.innerHTML = newElement.innerHTML;
            }
          }
        }
      });
      Logger.log('Cart drawer content refreshed via native method');
    } else {
      // Fallback: Replace cart drawer content directly
      if (data.sections && data.sections['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartDrawerSection = document.getElementById('CartDrawer');
        
        if (cartDrawerSection) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer') || doc.querySelector('.drawer__inner');
          
          if (newContent) {
            // Store scroll position and drawer state
            const drawerInner = cartDrawer?.querySelector('.drawer__inner');
            const scrollTop = drawerInner?.scrollTop || 0;
            const isActive = cartDrawer?.classList.contains('active');
            
            // Update content
            cartDrawerSection.innerHTML = newContent.innerHTML;
            
            // Restore state
            if (drawerInner) drawerInner.scrollTop = scrollTop;
            if (isActive && cartDrawer) {
              cartDrawer.classList.add('active', 'animate');
            }
            
            Logger.log('Cart drawer content refreshed via fallback');
          }
        }
      }
    }
    
    // Update cart icon bubble
    if (data.sections && data.sections['cart-icon-bubble']) {
      const cartBubbleSection = document.getElementById('cart-icon-bubble');
      if (cartBubbleSection) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        if (newBubble) {
          cartBubbleSection.innerHTML = newBubble.innerHTML;
        }
      }
    }
    
    // Update empty states
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerItems = cartDrawer?.querySelector('cart-drawer-items');
    const isEmpty = !drawerItems?.querySelector('.cart-item');
    
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
    
    return true;
  } catch (error) {
    Logger.error('Error refreshing cart drawer:', error);
    return false;
  }
}

// Main Cart Drawer Enhancements Manager
class CartDrawerEnhancements {
  constructor() {
    this.timer = new CartTimer();
    this.progressBar = new CartProgressBar();
    this.freeGiftManager = new FreeGiftManager();
    this.freeGiftDisplay = new FreeGiftDisplay();
    this.mysteryGiftManager = new MysteryGiftManager();
    this.itemCounter = new ItemCounter();
    this.cartWatcher = new CartWatcher();
    this.cartDrawer = null;
    this.lastCart = null;
    this.lastCartWatcherEventTime = 0; // Track when CartWatcher last emitted an event
    this.init();
  }

  init() {
    // Expose globally so cart.js can update state before section replacement
    window.cartEnhancements = this;
    
    // Store original fetch early (before any other scripts might intercept it)
    // This is used by CartWatcher and FreeGiftManager to avoid circular dependencies
    if (!window._originalFetch) {
      window._originalFetch = window.fetch.bind(window);
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  /**
   * Update state from cart data - called by cart.js BEFORE sections are replaced
   * This ensures MutationObserver applies the NEW state when DOM changes
   */
  updateState(cart) {
    const timestamp = Date.now();
    const hasGift = cart?.items?.some(item => 
      item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
      item.properties?._free_gift === 'true'
    );
    
    Logger.group(`[CartEnhancements][${timestamp}] 📥 updateState called from cart.js`);
    Logger.log('Cart data from cart.js response:', {
      itemCount: cart?.item_count,
      total: cart?.total_price ? (cart.total_price / 100).toFixed(2) : 'N/A',
      hasGift: hasGift,
      items: cart?.items?.map((item, idx) => ({
        index: idx,
        handle: item.handle,
        title: item.product_title,
        isFreeGift: item.properties?._free_gift === 'true',
        quantity: item.quantity,
        key: item.key
      }))
    });
    
    // Log if gift is present in cart.js response
    if (hasGift) {
      const giftItem = cart.items.find(item => 
        item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
        item.properties?._free_gift === 'true'
      );
      Logger.log('✅ FREE GIFT PRESENT in cart.js response:', {
        handle: giftItem.handle,
        key: giftItem.key,
        quantity: giftItem.quantity,
        note: 'CartWatcher will check if it stays after other scripts process'
      });
    } else {
      Logger.log('❌ FREE GIFT NOT in cart.js response');
    }
    
    // Check CartWatcher's last known state for comparison
    const cartWatcherStored = this.cartWatcher.getStoredCart();
    const cartWatcherHasGift = cartWatcherStored?.items?.some(item => 
      item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
      item.properties?._free_gift === 'true'
    );
    
    if (cartWatcherStored && cartWatcherStored.items && cartWatcherStored.items.length > 0) {
      Logger.log('📊 CartWatcher last known state:', {
        itemCount: cartWatcherStored.item_count,
        hasGift: cartWatcherHasGift,
        note: 'Will compare with CartWatcher when it detects change'
      });
    }
    
    Logger.groupEnd();
    
    if (!cart) return;
    
    // Store this cart data - CartWatcher will handle detecting changes
    this.lastCart = cart;
    
    // Update progress bar state (UI only, no cart modifications)
    this.progressBar.update(cart);
    
    // Update item counter state (UI only, no cart modifications)
    this.itemCounter.update(cart);
    
    // CartWatcher will detect cart changes and emit events
    // We don't need to process here - let CartWatcher handle it
  }

  setup() {
    this.cartDrawer = document.querySelector('cart-drawer');
    if (!this.cartDrawer) {
      setTimeout(() => this.setup(), 100);
      return;
    }

    // Initialize CartWatcher - this will detect all cart changes using PerformanceObserver
    this.cartWatcher.init();
    
    // Listen for cart_changed events from CartWatcher
    // This is much cleaner than intercepting fetch calls!
    this.lastCartWatcherEventTime = 0;
    window.addEventListener('cart_changed', (e) => {
      if (e.detail && e.detail.newCart) {
        const timestamp = Date.now();
        const newCart = e.detail.newCart;
        const changes = e.detail.changes || { added: [], removed: [] };
        
        // Check if we just completed a free gift operation (ignore our own changes)
        const timeSinceFreeGiftOp = timestamp - (this.freeGiftManager.lastOperationTime || 0);
        const freeGiftAdded = changes.added?.some(item => 
          item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties?._free_gift === 'true'
        );
        const freeGiftRemoved = changes.removed?.some(item => 
          item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
          item.properties?._free_gift === 'true'
        );
        
        // If we just added/removed the gift, ignore this event (it's our own change)
        if ((freeGiftAdded || freeGiftRemoved) && timeSinceFreeGiftOp < 3000) {
          Logger.log(`[CartEnhancements] Ignoring cart_changed event - we just ${freeGiftAdded ? 'added' : 'removed'} the gift ${timeSinceFreeGiftOp}ms ago`);
          return;
        }
        
        this.lastCartWatcherEventTime = timestamp;
        
        // Get triggering request info from event detail
        const triggeringRequest = e.detail.triggeringRequest;
        
        Logger.log('[CartEnhancements] cart_changed event received from CartWatcher:', {
          added: changes.added?.length || 0,
          removed: changes.removed?.length || 0,
          newItemCount: newCart.item_count,
          newTotal: (newCart.total_price / 100).toFixed(2),
          removedItems: changes.removed?.map(item => ({
            handle: item.handle,
            title: item.product_title,
            isFreeGift: item.properties?._free_gift === 'true'
          })) || [],
          freeGiftWasRemoved: freeGiftRemoved,
          triggeredBy: triggeringRequest ? {
            url: triggeringRequest.url,
            type: triggeringRequest.requestType || triggeringRequest.initiatorType,
            timeSinceRequest: (Date.now() - triggeringRequest.timestamp) + 'ms'
          } : 'unknown'
        });
        
        if (freeGiftRemoved) {
          Logger.group('[CartEnhancements] ⚠️ Free gift was removed by another script!');
          Logger.log('Investigation details:', {
            triggeringRequest: triggeringRequest ? {
              url: triggeringRequest.url,
              type: triggeringRequest.initiatorType,
              isUpdateRequest: triggeringRequest.url?.includes('/cart/update'),
              timestamp: new Date(triggeringRequest.timestamp).toISOString()
            } : 'No triggering request info',
            suspect: triggeringRequest?.url?.includes('/cart/update') ? 
              '⚠️ /cart/update.js - Likely MAKEBECOOL_BUNDLES or similar bundle app' : 
              'Investigate the request URL above'
          });
          Logger.log('Checking if it should be re-added...');
          Logger.groupEnd();
          
          // Calculate if gift should still be there
          const currency = newCart.currency || 'AUD';
          const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
          if (tiers) {
            // Calculate cart total excluding gift (already removed)
            const cartTotal = newCart.total_price;
            const regularItemCount = newCart.items.filter(item => 
              !(item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
                item.properties?._free_gift === 'true')
            ).length;
            
            const shouldHaveGift = cartTotal >= tiers.TIER_2 && regularItemCount > 0;
            
            if (shouldHaveGift) {
              Logger.log('[CartEnhancements] ✓ Cart still qualifies for gift, will re-add after delay');
            } else {
              Logger.log('[CartEnhancements] Cart no longer qualifies for gift (total too low or empty)');
            }
          }
        }
        
        // Delay longer to let other scripts (like MAKEBECOOL_BUNDLES) finish completely
        // Then update using the CartWatcher's fresh cart data
        setTimeout(async () => {
          // First, refresh the cart drawer's HTML content to show the new item.
          // We await this to ensure the DOM is updated before we proceed.
          await refreshCartDrawerWithReinit(newCart.item_count);
          // Then, update all the enhancement components (progress bar, etc.)
          this.onCartUpdate(newCart);
        }, 100); // Reduced delay for better performance
      }
    });

    // Listen for legacy cart:updated events (from other parts of the theme)
    window.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.cart) {
        setTimeout(() => this.onCartUpdate(e.detail.cart), 100);
      }
    });
    
    // Try to override cart drawer open method if it exists
    if (this.cartDrawer.open && typeof this.cartDrawer.open === 'function') {
      const originalOpen = this.cartDrawer.open.bind(this.cartDrawer);
      this.cartDrawer.open = (triggeredBy) => {
        originalOpen(triggeredBy);
        setTimeout(() => this.onCartOpen(), 300);
      };
    }
    
    // Watch for drawer becoming active (main method to detect open)
    const classObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (this.cartDrawer.classList.contains('active')) {
            setTimeout(() => this.onCartOpen(), 300);
          }
        }
      });
    });
    
    classObserver.observe(this.cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Initial check - if drawer is already active
    if (this.cartDrawer.classList.contains('active')) {
      setTimeout(() => this.onCartOpen(), 300);
    }
    
    // Fetch cart data once on initial load
    this.fetchAndUpdateCart();
  }
  
  async fetchAndUpdateCart() {
    try {
      const fetchFn = window._originalFetch || window.fetch;
      const response = await fetchFn(`${routes.cart_url}.js`);
      if (response.ok) {
        const cart = await response.json();
        this.lastCart = cart;
        await this.updateAll(cart);
      }
    } catch (error) {
      Logger.error('Error fetching cart:', error);
    }
  }

  async onCartOpen() {
    // Debounce rapid open events (can happen during DOM refresh)
    if (this._lastOpenTime && Date.now() - this._lastOpenTime < 500) {
      return;
    }
    this._lastOpenTime = Date.now();
    
    try {
      const fetchFn = window._originalFetch || window.fetch;
      const response = await fetchFn(`${routes.cart_url}.js`);
      if (response.ok) {
        const cart = await response.json();
        this.lastCart = cart;
        
        // Only initialize timer if cart has items
        if (cart && cart.item_count > 0) {
          this.timer.init();
        }
        
        this.updateAll(cart);
      }
    } catch (error) {
      Logger.error('Error fetching cart:', error);
      // Still try to update with default currency
      this.progressBar.update({ currency: 'AUD', item_count: 0, items: [], total_price: 0 });
    }
  }

  onCartUpdate(cart) {
    const timestamp = Date.now();
    
    // Skip if we just processed a free gift change (avoid loops)
    // Give it 2 seconds after free gift operations to avoid reacting to our own changes
    const timeSinceFreeGiftOp = timestamp - (this.freeGiftManager.lastOperationTime || 0);
    if (timeSinceFreeGiftOp < 2000) {
      Logger.log(`[CartEnhancements] Skipping update - free gift operation just completed ${timeSinceFreeGiftOp}ms ago`);
      return;
    }
    
    Logger.log(`[CartEnhancements][${timestamp}] onCartUpdate called:`, {
      itemCount: cart?.item_count,
      total: (cart?.total_price / 100).toFixed(2),
      hasGift: cart?.items?.some(item => 
        item.handle === CART_ENHANCEMENT_CONFIG.FREE_GIFT_HANDLE && 
        item.properties?._free_gift === 'true'
      )
    });
    
    this.lastCart = cart;
    this.updateAll(cart);
  }

  async updateAll(cart) {
    // Handle timer based on cart state
    if (!cart || cart.item_count === 0) {
      // Stop timer when cart is empty
      this.timer.stop();
    } else {
      // Start timer if cart has items and timer not running
      if (!this.timer.isRunning) {
        this.timer.init();
      }
    }
    
    // Update progress bar (auto-refreshes elements if DOM changed)
    this.progressBar.update(cart);
    
    // Update item counter
    this.itemCounter.reinitElements();
    this.itemCounter.update(cart);
    
    // Only check free gift if cart has items and manager isn't busy
    if (cart && cart.item_count > 0 && !this.freeGiftManager.isProcessing) {
      // Check and manage free gift (debounced internally)
      await this.freeGiftManager.checkAndManage(cart);
    }
    
    // Check and manage mystery gift
    await this.mysteryGiftManager.checkAndManage(cart);
  }
}

// Initialize the enhancements
window.cartDrawerEnhancements = new CartDrawerEnhancements();
